import { useCallback, useEffect, useState } from 'react'

import publishedCampaignSetupsApi from 'domain/publishedCampaignSetups/api'
import { findByAssetClass } from 'domain/integrationAssets/mappers'

// v4 GET /events/{id}/campaign_setups, shared by every consumer that used to
// read the deprecated campaign.latest_campaign_setup field directly. One
// fetch per mounted consumer, matching this codebase's existing
// per-component fetch pattern rather than a shared-cache layer.
export function usePublishedCampaignSetups(eventId) {
  const [state, setState] = useState({
    loading: true,
    eventCampaign: null,
    tasteClusters: [],
    error: null,
  })

  const fetchAndSetState = useCallback(
    (isCancelled = () => false) => {
      if (!eventId) return Promise.resolve()

      return publishedCampaignSetupsApi.fetchCampaignSetupsAsync(eventId).then(({ data, error }) => {
        if (isCancelled()) return
        setState({
          loading: false,
          eventCampaign: data?.eventCampaign ?? null,
          tasteClusters: data?.tasteClusters ?? [],
          error: error ?? null,
        })
      })
    },
    [eventId],
  )

  useEffect(() => {
    if (!eventId) return

    let cancelled = false
    setState({ loading: true, eventCampaign: null, tasteClusters: [], error: null })
    fetchAndSetState(() => cancelled)

    return () => {
      cancelled = true
    }
  }, [eventId, fetchAndSetState])

  // Neither v4 write endpoint returns a body — this is the only way a
  // caller sees the persisted result.
  const refetch = useCallback(() => fetchAndSetState(), [fetchAndSetState])

  return { ...state, refetch }
}

export function findTasteCluster(tasteClusters, tc, tcRunId) {
  return tasteClusters.find((entry) => entry.tc === tc && entry.tcRunId === tcRunId) ?? null
}

// eventCampaign.platforms and campaignSetups[].channel both use short codes
// ('fb'/'google') — a different vocabulary from setup_process's `platform`
// column ('facebook'/'google'), which derivePlatformSelectionFromSetup expects.
const SHORT_CODE_TO_PLATFORM_FIELD = { fb: 'facebook', google: 'google' }

export function toSetupPlatformField(platforms = []) {
  return platforms.map((code) => SHORT_CODE_TO_PLATFORM_FIELD[code]).filter(Boolean)
}

// eventCampaign.platforms is the event-wide integration_details union, which
// over-reports for a legacy synthesized TC — fall back to it only when the TC
// has no rows of its own.
export function tcPlatformField(tasteCluster, eventCampaign) {
  const channels = (tasteCluster?.campaignSetups ?? []).map((setup) => setup.channel)
  return channels.length
    ? toSetupPlatformField(channels)
    : toSetupPlatformField(eventCampaign?.platforms)
}

// Reshapes a v4 taste cluster + event_campaign pair back into the {creative,
// targeting, integration_details} shape callers previously read off
// campaign.latest_campaign_setup, so downstream consumers don't need their
// own translation layer.
export function toCampaignSetupCompat(tasteCluster, eventCampaign) {
  if (!tasteCluster) return null

  const integrationDetails = eventCampaign?.integrationDetails ?? []
  // Both drive goal/lead-form-scoped creative rules (the call-to-action option
  // list, the one-language-only lead form rule). Derived here the same way
  // mapSetupProcessResponse derives them pre-publish, so an edit on a
  // published TC resolves them against the same objective it was published
  // under instead of falling back to the event's default goal.
  const leadForm = findByAssetClass(integrationDetails, {
    platform: 'facebook',
    assetClass: 'lead_form',
  })

  return {
    integration_details: integrationDetails,
    goal: eventCampaign?.goal ?? null,
    lead_form_id: leadForm?.asset_id,
    platform_overrides: eventCampaign?.platformOverrides ?? null,
    campaign_targeting: eventCampaign?.campaignTargeting ?? null,
    platform: tcPlatformField(tasteCluster, eventCampaign),
    creative: tasteCluster.creative,
    targeting: [
      {
        tc: tasteCluster.tc,
        tc_run_id: tasteCluster.tcRunId,
        ...tasteCluster.normalizedTargeting,
      },
    ],
  }
}
